/**
 * Decides where the root "/" route sends a just-authenticated user, mirroring
 * the WinForms "Startup Form" preference (`tblPMSec_User.DF_Start_Form`).
 *
 * Extracted as a pure function so the routing rules are unit-testable without
 * mounting the whole auth/router/store stack.
 *
 * Ordering matters and encodes a real production bug (fixed 2026-08-04):
 *   1. No connection selected yet → the connection picker. The connection lives
 *      in sessionStorage, so every fresh browser session/tab starts with none.
 *      `StartFormRedirect` renders OUTSIDE `MainLayout`, so `MainLayout`'s own
 *      `!selectedConnection → /connection` guard never runs at "/". Without this
 *      first branch the user sticks on the loading screen forever, because
 *      facility/user IDs never resolve until a connection provides the
 *      X-Connection-Id header that the `/Me` lookup requires.
 *   2. Signing in stopped: the user has no facility mappings, or their facility
 *      list / role could not be read → into the layout, which says so (No facility
 *      access, or the error with Try again). These never resolve a facility, so
 *      waiting for one left the user on "Loading application..." for good.
 *   3. Connection selected but user/facility/role not resolved yet → loading.
 *   4. Resolved → the WinForms startup rules (frmMain.LoadDefaultStartForm and
 *      frmDietech_Resident_DataGrid_V2's `_loadStartupForm` block):
 *      a. Selective Touch level 1 or 2 ("Direct Load") opens Selective Touch on
 *         the employee list whatever the start form says;
 *      b. otherwise the start form, but only when the role can open it. WinForms
 *         always loads the resident grid first and opens the start form over it,
 *         so a form the user can't open leaves them on the resident grid.
 */
export type StartRoute =
  | { kind: 'redirect'; to: string }
  | { kind: 'loading' };

/** The role levels the startup rules read. */
export interface StartRouteRole {
  dietech_Level?: number | null;
  dietech_Selective_Menu_Touch_Level?: number | null;
}

/**
 * The `DF_Start_Form` values the website can open. WinForms also offers
 * 0 "None" (which opens the resident grid, same as 1) and the purchasing forms
 * in `DESKTOP_ONLY_START_FORMS`, which have no page here and open the resident
 * list instead.
 */
export const START_FORM = { RESIDENTS: 1, SELECTIVE_TOUCH: 2, DASHBOARD: 6 } as const;

/** WinForms `DF_Start_Form` values for the purchasing forms the website doesn't have. */
export const DESKTOP_ONLY_START_FORMS: Record<number, string> = {
  3: 'Purchasing: Order Forms',
  4: 'Purchasing: PO Review',
  5: 'Purchasing: Quick Order',
};

const RESIDENTS_ROUTE = '/people';
/**
 * Where a user goes when signing in cannot finish. Any page inside MainLayout
 * would do: with no facility it shows the No facility access message (or the
 * sign-in error) instead of the page. The dashboard is the one with no role gate.
 */
export const SIGN_IN_BLOCKED_ROUTE = '/dashboard';
const SELECTIVE_TOUCH_ROUTE = '/selective-touch';
/** Selective Touch's employee list (its `employee` page). */
const SELECTIVE_TOUCH_EMPLOYEE_ROUTE = '/selective-touch/employee';

/** Selective Touch levels 1 and 2 are the employee "Direct Load" levels (frmUser_Roles_AddMod). */
function isTouchDirectLoad(role: StartRouteRole): boolean {
  const touch = role.dietech_Selective_Menu_Touch_Level ?? 0;
  return touch === 1 || touch === 2;
}

/** WinForms opens Selective Touch at startup only with Dietech and Selective Touch access. */
function canOpenSelectiveTouch(role: StartRouteRole): boolean {
  return (role.dietech_Level ?? 0) > 0 && (role.dietech_Selective_Menu_Touch_Level ?? 0) > 0;
}

/**
 * The page a resolved user starts on. `role` null means the role could not be
 * read: the start form is used as saved and the page's own route guard decides.
 */
export function startRouteFor(startForm: number | null | undefined, role: StartRouteRole | null): string {
  if (role && isTouchDirectLoad(role)) return SELECTIVE_TOUCH_EMPLOYEE_ROUTE;
  switch (startForm) {
    case START_FORM.SELECTIVE_TOUCH:
      return !role || canOpenSelectiveTouch(role) ? SELECTIVE_TOUCH_ROUTE : RESIDENTS_ROUTE;
    case START_FORM.DASHBOARD:
      return '/dashboard';
    default:
      return RESIDENTS_ROUTE;
  }
}

export function resolveStartRoute(args: {
  hasConnection: boolean;
  facilityId: number;
  userId: number;
  userLoaded: boolean;
  startForm: number | null | undefined;
  /**
   * The user's role levels. `undefined` while the role is still loading; `null`
   * when it could not be read; a user with no role at all passes `{}`.
   */
  role: StartRouteRole | null | undefined;
  /** The user has no usable facility mappings (globalStore.noFacilityAccess). */
  noFacilityAccess?: boolean;
  /** Signing in stopped on an error (globalStore.sessionLoadError). */
  sessionLoadError?: string | null;
}): StartRoute {
  if (!args.hasConnection) return { kind: 'redirect', to: '/connection' };
  if (args.noFacilityAccess || args.sessionLoadError) return { kind: 'redirect', to: SIGN_IN_BLOCKED_ROUTE };
  if (args.facilityId <= 0 || args.userId <= 0 || !args.userLoaded || args.role === undefined) {
    return { kind: 'loading' };
  }
  return { kind: 'redirect', to: startRouteFor(args.startForm, args.role) };
}
